import { BLOCK_INDENT_TYPES, MAX_BLOCK_INDENT } from "@corca/tiptap";
import { isInTable } from "@tiptap/pm/tables";

import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";

import type { Editor } from "@tiptap/core";
import type { Node as ProsemirrorNode, ResolvedPos } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";

type IndentAttrs = {
  indent: number;
  firstLineIndent: boolean;
};

const LIST_ITEM_TYPES = ["listItem", "taskItem"];

/** These containers own Tab themselves (nesting, cell navigation). */
const INDENT_BLOCKING_ANCESTORS = [...LIST_ITEM_TYPES, "tableCell", "tableHeader"];

function isIndentable($pos: ResolvedPos): boolean {
  const { parent } = $pos;

  if (!BLOCK_INDENT_TYPES.includes(parent.type.name) || !("indent" in parent.attrs)) {
    return false;
  }

  for (let depth = $pos.depth - 1; depth > 0; depth -= 1) {
    if (INDENT_BLOCKING_ANCESTORS.includes($pos.node(depth).type.name)) {
      return false;
    }
  }

  return true;
}

function indentAttrs(node: ProsemirrorNode): IndentAttrs {
  return node.attrs as IndentAttrs;
}

export function isIndented(node: ProsemirrorNode): boolean {
  const { indent, firstLineIndent } = indentAttrs(node);
  return indent > 0 || !!firstLineIndent;
}

function setIndent(tr: Transaction, pos: number, indent: number) {
  return tr.setNodeAttribute(pos, "indent", Math.min(Math.max(indent, 0), MAX_BLOCK_INDENT));
}

/**
 * Tab / Shift-Tab in a list item, following Google Docs. Tab nests the item under the one above,
 * or, where it can't (a list's first item), indents the list it is in. Shift-Tab undoes the two in
 * reverse: it removes that list's indent, then lifts the item out a level. Either way the key is
 * taken, so it never moves focus out of the editor.
 *
 * In a table cell the keys are left to the list's own nesting and then to cell navigation.
 */
export function handleListTab(editor: Editor, direction: "indent" | "outdent"): boolean {
  const { state, view } = editor;
  const { $head } = state.selection;

  if (isInTable(state)) {
    return false;
  }

  for (let { depth } = $head; depth > 1; depth -= 1) {
    const { name: itemType } = $head.node(depth).type;

    if (LIST_ITEM_TYPES.includes(itemType)) {
      const listPos = $head.before(depth - 1);
      const { indent } = indentAttrs($head.node(depth - 1));

      if (direction === "indent") {
        if (!editor.commands.sinkListItem(itemType)) {
          view.dispatch(setIndent(state.tr, listPos, indent + 1));
        }
      } else if (indent > 0) {
        view.dispatch(setIndent(state.tr, listPos, indent - 1));
      } else {
        editor.commands.liftListItem(itemType);
      }

      return true;
    }
  }

  return false;
}

/**
 * Tab / Shift-Tab in a paragraph, following Google Docs.
 *
 * - adds indent at the start of a paragraph - to the first line or the whole block;
 * - elsewere inserts a tab character.
 */
export function handleIndentTab(editor: Editor, direction: "indent" | "outdent"): boolean {
  const { state, view } = editor;
  const { selection, tr } = state;
  const step = direction === "indent" ? 1 : -1;

  if (!selection.empty || direction === "outdent") {
    let handled = false;

    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (!node.isTextblock) {
        return true;
      }

      if (isIndentable(state.doc.resolve(pos + 1))) {
        handled = true;
        const { indent, firstLineIndent } = indentAttrs(node);

        if (step < 0 && indent === 0) {
          if (firstLineIndent) {
            tr.setNodeAttribute(pos, "firstLineIndent", false);
          }
        } else {
          setIndent(tr, pos, indent + step);
        }
      }

      return false;
    });

    if (handled) {
      view.dispatch(tr);
    }

    return handled;
  }

  const { $head } = selection;

  if (!isIndentable($head)) {
    return false;
  }

  if ($head.parentOffset > 0) {
    view.dispatch(tr.insertText("\t").scrollIntoView());
    return true;
  }

  const { indent, firstLineIndent } = indentAttrs($head.parent);
  // A first-line indent would shift a leading equation off the column that math
  // auto-indentation aligns it to, so such a block only gets the block indent.
  const startsWithMath = nodeIsExpression($head.nodeAfter);

  if (firstLineIndent || startsWithMath) {
    setIndent(tr, $head.before(), indent + 1);
  } else {
    tr.setNodeAttribute($head.before(), "firstLineIndent", true);
  }

  view.dispatch(tr);
  return true;
}

/**
 * Removes the first-line indent if there is one, otherwise one step of the block indent.
 */
export function removeIndentAtBlockStart(editor: Editor): boolean {
  const { state, view } = editor;
  const { selection, tr } = state;
  const { $head } = selection;

  if (!selection.empty || $head.parentOffset > 0 || !isIndentable($head)) {
    return false;
  }

  const { indent, firstLineIndent } = indentAttrs($head.parent);

  if (firstLineIndent) {
    tr.setNodeAttribute($head.before(), "firstLineIndent", false);
  } else if (indent > 0) {
    setIndent(tr, $head.before(), indent - 1);
  } else {
    return false;
  }

  view.dispatch(tr);
  return true;
}
